import { COLLISION_CELLS_PER_TILE } from "daedalus-core";
import { spawnAreaCells } from "../creatures/spawn.ts";
import type { NpcCell } from "../npcs/marker.ts";
import type { FieldMarker } from "./field-marker.ts";

/**
 * A field's cells on the crop-cell grid (ADR-144 decision 6): the 32 px cells a crop is planted in, laid on the map
 * from its top-left corner, the connected pieces a field is made of, and the patches a rental piece divides into.
 * Pure: the editor's Issues list and the compile read a field the same way.
 */

/**
 * The size of a crop cell in pixels, the size of the pack's crop frames.
 */
export const FIELD_CELL_PX = 32;

/**
 * One crop cell by its column and row on the map's grid of crop cells, counted from the map's top-left corner.
 */
export interface CropCell {
  readonly col: number;
  readonly row: number;
}

/**
 * One connected piece of a field: the cells it holds in reading order, the top-left corner and size of the box that
 * bounds it, and whether it fills that box.
 */
export interface FieldShape {
  readonly col: number;
  readonly row: number;
  readonly width: number;
  readonly height: number;
  readonly cells: readonly CropCell[];
  readonly full: boolean;
}

/**
 * The tiles a crop cell is wide and tall.
 *
 * @param tileSize The world's tile size in pixels.
 * @returns The count, or null when a crop cell is not a whole number of tiles.
 */
export function tilesPerCropCell(tileSize: number): number | null {
  return Number.isInteger(tileSize) && tileSize >= 1 && FIELD_CELL_PX % tileSize === 0 ? FIELD_CELL_PX / tileSize : null;
}

/**
 * The crop cells a field covers: every crop cell holding a painted collision cell, so an edge that cuts a crop cell
 * takes the whole of it. A painted cell is `[column, rowsUp]` from the bottom-left of its marker's tile, laid on the
 * map's grid of collision cells as the server lays a spawn area (`spawnAreaCells`).
 *
 * @param marker The marker, whose point names the tile the cells are counted from.
 * @param cells The painted cells.
 * @param tileSize The world's tile size in pixels, one that divides `FIELD_CELL_PX`.
 * @returns Each crop cell once, in reading order; a cell left of or above the map has a negative column or row.
 * @throws Error when the tile size does not divide the crop cell.
 */
export function cropCellsOf(marker: Pick<FieldMarker, "x" | "y">, cells: readonly NpcCell[], tileSize: number): CropCell[] {
  const per = tilesPerCropCell(tileSize);
  if (per === null) {
    throw new Error(`a tile of ${tileSize} px does not divide a crop cell of ${FIELD_CELL_PX} px`);
  }
  const seen = new Map<string, CropCell>();
  for (const [column, row] of spawnAreaCells(marker.x, marker.y, cells)) {
    const cell = { col: Math.floor(column / COLLISION_CELLS_PER_TILE / per), row: Math.floor(row / COLLISION_CELLS_PER_TILE / per) };
    seen.set(`${cell.col},${cell.row}`, cell);
  }
  return [...seen.values()].sort((a, b) => a.row - b.row || a.col - b.col);
}

/**
 * Splits crop cells into their connected pieces, two cells being connected when they share an edge.
 *
 * @param cells The cells, each once.
 * @returns The pieces, in reading order of their top-left cell.
 */
export function shapesOf(cells: readonly CropCell[]): FieldShape[] {
  const left = new Map(cells.map((cell) => [`${cell.col},${cell.row}`, cell]));
  const shapes: FieldShape[] = [];
  for (const start of cells) {
    if (!left.delete(`${start.col},${start.row}`)) {
      continue;
    }
    const piece: CropCell[] = [];
    const queue: CropCell[] = [start];
    for (let next = queue.pop(); next !== undefined; next = queue.pop()) {
      piece.push(next);
      for (const [dc, dr] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
        const key = `${next.col + dc},${next.row + dr}`;
        const neighbour = left.get(key);
        if (neighbour !== undefined) {
          left.delete(key);
          queue.push(neighbour);
        }
      }
    }
    piece.sort((a, b) => a.row - b.row || a.col - b.col);
    let col = Infinity;
    let row = Infinity;
    let lastCol = -Infinity;
    let lastRow = -Infinity;
    for (const cell of piece) {
      col = Math.min(col, cell.col);
      row = Math.min(row, cell.row);
      lastCol = Math.max(lastCol, cell.col);
      lastRow = Math.max(lastRow, cell.row);
    }
    const width = lastCol - col + 1;
    const height = lastRow - row + 1;
    shapes.push({ col, row, width, height, cells: piece, full: piece.length === width * height });
  }
  return shapes.sort((a, b) => a.row - b.row || a.col - b.col);
}

/**
 * What a rental field carves into: the patches, each by its top-left cell in reading order, and how many painted cells
 * belong to none.
 */
export interface CarvedPatches {
  readonly patches: readonly CropCell[];
  readonly leftover: number;
}

/**
 * Carves a rental field's cells into patches (ADR-144 decision 6, as amended): each connected piece gets a grid of
 * `patchSize` squares laid from its first cell in reading order, extended in every direction, and every square that
 * lies wholly inside the piece is a patch. The same cells always carve the same patches. Cells in no patch are not
 * plots.
 *
 * @param cells The field's crop cells, each once.
 * @param patchSize The cells a patch is wide and tall.
 * @returns The patches in reading order, and the count of cells in none.
 */
export function carvePatches(cells: readonly CropCell[], patchSize: number): CarvedPatches {
  const patches: CropCell[] = [];
  for (const shape of shapesOf(cells)) {
    const held = new Set(shape.cells.map((cell) => `${cell.col},${cell.row}`));
    const origin = shape.cells[0] as CropCell;
    for (const cell of shape.cells) {
      if ((cell.col - origin.col) % patchSize !== 0 || (cell.row - origin.row) % patchSize !== 0) {
        continue;
      }
      let whole = true;
      for (let dy = 0; dy < patchSize && whole; dy += 1) {
        for (let dx = 0; dx < patchSize; dx += 1) {
          if (!held.has(`${cell.col + dx},${cell.row + dy}`)) {
            whole = false;
            break;
          }
        }
      }
      if (whole) {
        patches.push(cell);
      }
    }
  }
  patches.sort((a, b) => a.row - b.row || a.col - b.col);
  return { patches, leftover: cells.length - patches.length * patchSize * patchSize };
}
